/**
 * 菜单配置和权限逻辑
 */
import type { MenuProps } from 'antd';
import {
  DashboardOutlined,
  ProjectOutlined,
  ClockCircleOutlined,
  ToolOutlined,
  ShoppingOutlined,
  BarChartOutlined,
  SettingOutlined,
  DatabaseOutlined,
  DollarOutlined,
  TeamOutlined,
  SafetyOutlined,
  ApartmentOutlined,
  FormOutlined,
  PlusSquareOutlined,
  WarningOutlined,
  PieChartOutlined,
  InboxOutlined,
  SwapOutlined,
  CheckCircleOutlined,
  UserOutlined,
  HistoryOutlined,
  FileTextOutlined,
  ShopOutlined,
} from '@ant-design/icons';

type MenuItem = Required<MenuProps>['items'][number];

interface MenuItemConfig extends MenuItem {
  permission?: string | string[] | null;
  children?: MenuItemConfig[];
}

// 菜单项配置（带权限）
const menuConfigs: MenuItemConfig[] = [
  {
    key: '/dashboard',
    icon: <DashboardOutlined />,
    label: '项目总览',
    permission: null,  // 所有人可见
  },
  {
    key: '/project',
    icon: <ProjectOutlined />,
    label: '项目管理',
    permission: 'project:view',
  },
  {
    key: 'worker',
    icon: <TeamOutlined />,
    label: '工人记录与管理',
    permission: null,
    children: [
      {
        key: '/worker-manage',
        icon: <UserOutlined />,
        label: '工人列表',
        permission: 'worker:view',
      },
      {
        key: '/worker-history',
        icon: <HistoryOutlined />,
        label: '出工记录',
        permission: 'worker:view',
      },
      {
        key: '/batch-semantic-parse',
        icon: <FormOutlined />,
        label: '工时批量录入',
        permission: 'worktime:create',
      },
      {
        key: '/worktime-batch-history',
        icon: <HistoryOutlined />,
        label: '批量提交历史',
        permission: 'worktime:view',
      },
    ],
  },
  {
    key: 'material-records',
    icon: <ShoppingOutlined />,
    label: '材料记录与管理',
    permission: null,
    children: [
      {
        key: '/recognition-management',
        icon: <FileTextOutlined />,
        label: '材料识别',
        permission: 'material:view',
      },
      {
        key: '/material',
        icon: <ShoppingOutlined />,
        label: '材料使用',
        permission: 'material:view',
      },
      {
        key: '/batch-material-parse',
        icon: <FormOutlined />,
        label: '材料批量录入',
        permission: 'material:create',
      },
      {
        key: '/transfer',
        icon: <SwapOutlined />,
        label: '材料调拨',
        permission: 'transfer:view',
      },
      {
        key: '/inventory',
        icon: <InboxOutlined />,
        label: '库存管理',
        permission: 'inventory:view',
      },
    ],
  },
  // 已隐藏：工时记录
  // {
  //   key: '/worktime',
  //   icon: <ClockCircleOutlined />,
  //   label: '工时记录',
  //   permission: 'worktime:view',
  // },
  {
    key: '/equipment',
    icon: <ToolOutlined />,
    label: '设备使用',
    permission: 'equipment:view',
  },
  {
    key: 'progress',
    icon: <ApartmentOutlined />,
    label: '进度跟踪',
    permission: 'progress:view',
    children: [
      {
        key: '/progress/work-items',
        icon: <ApartmentOutlined />,
        label: '工程项管理',
        permission: 'progress:view',
      },
      {
        key: '/progress/records',
        icon: <FormOutlined />,
        label: '进度填报',
        permission: 'progress:view',
      },
      {
        key: '/progress/additions',
        icon: <PlusSquareOutlined />,
        label: '增项管理',
        permission: 'progress:view',
      },
      {
        key: '/progress/rectifications',
        icon: <WarningOutlined />,
        label: '整改管理',
        permission: 'progress:view',
      },
      {
        key: '/progress/reports/progress',
        icon: <PieChartOutlined />,
        label: '进度报表',
        permission: 'report:view',
      },
      {
        key: '/progress/reports/additions',
        icon: <BarChartOutlined />,
        label: '增项报表',
        permission: 'report:view',
      },
      {
        key: '/progress/reports/rectifications',
        icon: <BarChartOutlined />,
        label: '整改报表',
        permission: 'report:view',
      },
    ],
  },
  {
    key: '/reports',
    icon: <BarChartOutlined />,
    label: '报表分析',
    permission: 'report:view',
  },
  {
    key: 'reports-new',
    icon: <PieChartOutlined />,
    label: '报表中心',
    permission: 'report:view',
    children: [
      {
        key: '/reports-new/dashboard',
        icon: <DashboardOutlined />,
        label: '经营分析',
        permission: 'report:dashboard:view',
      },
      {
        key: '/reports-new/cost',
        icon: <DollarOutlined />,
        label: '成本报表',
        permission: 'report:cost:view',
      },
      {
        key: '/reports-new/progress',
        icon: <ApartmentOutlined />,
        label: '进度报表',
        permission: 'report:progress:view',
      },
      {
        key: '/reports-new/material',
        icon: <ShoppingOutlined />,
        label: '材料报表',
        permission: 'report:material:view',
      },
      {
        key: '/reports-new/worktime',
        icon: <ClockCircleOutlined />,
        label: '工时报表',
        permission: 'report:worktime:view',
      },
      {
        key: '/reports-new/inventory',
        icon: <InboxOutlined />,
        label: '库存报表',
        permission: 'report:inventory:view',
      },
      {
        key: '/reports-new/equipment',
        icon: <ToolOutlined />,
        label: '设备报表',
        permission: 'report:equipment:view',
      },
      {
        key: '/reports-new/change-order',
        icon: <PlusSquareOutlined />,
        label: '增项报表',
        permission: 'report:changeorder:view',
      },
      {
        key: '/reports-new/rectification',
        icon: <WarningOutlined />,
        label: '整改报表',
        permission: 'report:rectification:view',
      },
      {
        key: '/reports-new/transfer',
        icon: <SwapOutlined />,
        label: '调拨报表',
        permission: 'report:transfer:view',
      },
    ],
  },
  {
    key: 'admin',
    icon: <SettingOutlined />,
    label: '系统管理',
    permission: 'system:manage',
    children: [
      {
        key: '/data-management',
        icon: <DatabaseOutlined />,
        label: '数据管理',
        permission: 'system:manage',
      },
      {
        key: '/price-config',
        icon: <DollarOutlined />,
        label: '价格配置',
        permission: 'system:manage',
      },
      {
        key: '/user-management',
        icon: <TeamOutlined />,
        label: '用户管理',
        permission: 'system:manage',
      },
      {
        key: '/user-permission-manage',
        icon: <SafetyOutlined />,
        label: '用户权限管理',
        permission: 'system:manage',
      },
      {
        key: '/permission-matrix',
        icon: <SafetyOutlined />,
        label: '权限矩阵',
        permission: 'system:manage',
      },
      {
        key: '/data-validation',
        icon: <CheckCircleOutlined />,
        label: '数据验证',
        permission: 'system:manage',
      },
      {
        key: '/supplier-management',
        icon: <ShopOutlined />,
        label: '供应商管理',
        permission: 'supplier:manage',
      },
    ],
  },
];

/**
 * 权限检查函数
 */
const checkPermission = (permission: string | string[] | null | undefined, userPermissions: string[]): boolean => {
  if (!permission) return true;  // 无权限要求，允许访问
  
  const perms = Array.isArray(permission) ? permission : [permission];
  return perms.some(p => {
    // 直接匹配
    if (userPermissions.includes(p)) return true;
    
    const parts = p.split(':');
    const resource = parts[0];
    const subResource = parts.length === 3 ? parts[1] : null;

    // 检查子资源 manage（如 report:cost:manage 覆盖 report:cost:view）
    if (subResource && userPermissions.includes(`${resource}:${subResource}:manage`)) return true;

    // 检查父资源 manage（如 report:manage 覆盖 report:cost:view）
    if (userPermissions.includes(`${resource}:manage`)) return true;

    // system:manage 超级管理员覆盖一切
    if (userPermissions.includes('system:manage')) return true;
    
    return false;
  });
};

/**
 * 递归过滤菜单项
 */
const filterMenuItems = (items: MenuItemConfig[], userPermissions: string[]): MenuItem[] => {
  return items
    .filter(item => checkPermission(item.permission, userPermissions))
    .map(item => {
      if (item.children) {
        const filteredChildren = filterMenuItems(item.children, userPermissions);
        // 如果子菜单全部被过滤掉，则不显示父菜单
        if (filteredChildren.length === 0) {
          return null;
        }
        return {
          ...item,
          children: filteredChildren
        };
      }
      return item;
    })
    .filter(Boolean) as MenuItem[];
};

/**
 * 获取过滤后的菜单项
 */
export const getMenuItems = (userPermissions?: string[]): MenuItem[] => {
  // 如果没有权限信息，只显示无权限要求的菜单
  if (!userPermissions || userPermissions.length === 0) {
    return menuConfigs.filter(item => !item.permission) as MenuItem[];
  }

  const filteredMenus = filterMenuItems(menuConfigs, userPermissions);
  console.log('[Menu Debug] userPermissions:', userPermissions);
  console.log('[Menu Debug] filteredMenus:', filteredMenus);
  
  return filteredMenus;
};
